import { Maximize2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { CompareImages } from './types';

interface SideBySideViewProps extends CompareImages {
  fullscreen?: boolean;
  onExpand?: () => void;
}

export function SideBySideView({ originalImage, renderImage, fullscreen = false, onExpand }: SideBySideViewProps) {
  const panel = cn(
    'relative h-full overflow-hidden flex flex-col items-center justify-center',
    fullscreen ? 'rounded-xl bg-slate-950' : 'rounded-lg bg-slate-900',
  );
  const label = fullscreen
    ? 'top-3 left-3 text-xs px-3 py-1 rounded-lg shadow-lg'
    : 'top-2 left-2 text-[10px] px-2 py-0.5 rounded';
  const image = cn('max-h-full max-w-full object-contain', fullscreen && 'p-2');

  return (
    <div className={cn('relative group w-full', fullscreen ? 'h-full' : 'p-3 h-[450px]')}>
      <div className={cn('grid grid-cols-1 md:grid-cols-2 w-full h-full', fullscreen ? 'gap-4' : 'gap-3')}>
        <div className={cn(panel, 'border border-slate-800')}>
          <span className={cn('absolute bg-slate-950/80 text-slate-200 font-bold border border-slate-700 z-10', label)}>
            {fullscreen ? 'BEFORE: RAW PROPERTY PHOTO' : 'BEFORE: RAW'}
          </span>
          <img src={originalImage} alt="Before: raw property photo" className={image} referrerPolicy="no-referrer" />
        </div>
        <div className={cn(panel, fullscreen ? 'border border-blue-500/50' : 'border border-blue-500/40')}>
          <span className={cn('absolute bg-blue-600 text-white font-bold z-10 flex items-center gap-1.5', label)}>
            {fullscreen && <Sparkles className="w-3.5 h-3.5" />} AFTER: MAKEOVER RENDER
          </span>
          <img src={renderImage} alt="After makeover render" className={image} referrerPolicy="no-referrer" />
        </div>
      </div>

      {onExpand && (
        <button
          type="button"
          onClick={onExpand}
          className="absolute bottom-5 right-5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-2xl border border-blue-400/30 flex items-center gap-1.5 transition-all opacity-90 group-hover:opacity-100 hover:scale-105 z-20"
        >
          <Maximize2 className="w-4 h-4" /> Full-Screen Dual View
        </button>
      )}
    </div>
  );
}
